import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Gift, Gem, MapPin, Clock, Shield, Lock, Loader2, CheckCircle2, AlertTriangle, Download, ChevronDown, ChevronUp, TrendingUp, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import { useGoldRate } from '../context/GoldRateContext';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';

// Rotating tips for Redeem Gold page
const redeemGoldTips = [
  "Tip: Vouchers are valid for 30 days — plan your showroom visit",
  "Tip: Choose from a wide range of 22K BIS hallmarked jewellery",
  "Tip: No making charges deducted from your gold weight",
  "Tip: Visit any Mothers' Gold showroom with your voucher ID",
  "Tip: You can redeem partial Milli's and save the rest for later",
  "Tip: Gold jewellery makes a timeless gift for loved ones",
  "Tip: Our showroom staff will help you pick the perfect design",
  "Tip: Minimum 1 gram (1000 Milli's) required for jewellery redemption",
];

const RedeemGoldPage = () => {
  const navigate = useNavigate();
  const { isAuthenticated, loading: authLoading, user, refreshUser } = useAuth();
  const { sellRate, loading: rateLoading } = useGoldRate();
  
  const [millis, setMillis] = useState(1000);
  const [customMillis, setCustomMillis] = useState('1000');
  const [redeemOption, setRedeemOption] = useState('jewellery');
  const [isLoading, setIsLoading] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [redeemResult, setRedeemResult] = useState(null);
  const [showShowrooms, setShowShowrooms] = useState(false);
  const [expandedShowroom, setExpandedShowroom] = useState(null);
  
  // Random tip on page visit
  const [currentTip] = useState(() => redeemGoldTips[Math.floor(Math.random() * redeemGoldTips.length)]);

  // Redirect unauthenticated users to home-new silently
  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/home-new', { replace: true });
    }
  }, [isAuthenticated, authLoading, navigate]);

  // Show nothing while checking auth (prevents flash of content)
  if (authLoading || !isAuthenticated) {
    return null;
  }

  const presetMillis = [1000, 2000, 5000, 10000];
  const userMilliBalance = user?.milli_balance || 0;
  const userMilliInGrams = userMilliBalance / 1000;
  const millisInGrams = millis / 1000;
  const goldValue = millis * (sellRate || 0);
  // Use kyc_verified flag or aadhaar_last_4 presence instead of full aadhaar
  const hasKYC = !!user?.kyc_verified || !!user?.aadhaar_last_4;
  const canRedeem = hasKYC && millis >= 1000 && millis <= userMilliBalance;

  const handleMillisChange = (value) => {
    const numValue = parseFloat(value) || 0;
    setMillis(numValue);
    setCustomMillis(value);
  };

  const handlePresetClick = (preset) => {
    setMillis(preset);
    setCustomMillis(preset.toString());
  };

  const handleContinue = () => {
    if (!hasKYC) {
      toast.error('KYC verification required. Please complete Aadhaar verification first.');
      navigate('/kyc-new');
      return;
    }
    if (millis < 1000) {
      toast.error('Minimum 1000 Milli (1 gram) required for redemption');
      return;
    }
    if (millis > userMilliBalance) {
      toast.error('Insufficient Milli balance');
      return;
    }
    setShowConfirmModal(true);
  };

  const handleConfirmRedeem = async () => {
    setIsLoading(true);
    setShowConfirmModal(false);
    
    try {
      const response = await api.post('/milli/redeem', {
        milli_amount: millis
      });
      
      setRedeemResult(response.data);
      toast.success('Gold voucher created successfully!');
      
      if (refreshUser) {
        await refreshUser();
      }
    } catch (error) {
      console.error('Redeem error:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to create voucher. Please try again.';
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleNewRedeem = () => {
    setRedeemResult(null);
    setMillis(1000);
    setCustomMillis('1000');
  };

  // Showroom locations
  const showrooms = [
    { 
      name: "Palakkad", 
      address: "Abba Tower, English Church Rd, near Mission School, Palakkad, Keralam 678014" 
    },
    { 
      name: "Perinthalmanna", 
      address: "32/1626/23, Aysha Trade Avenue Opp: Vavas Mall, Padippura, Palakkad Byepass Road, Malappuram, Kozhikode, Perinthalmanna, Keralam 679322" 
    },
  ];

  return (
    <>
      {/* Success Result Screen */}
      {redeemResult && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
            <div className="text-center">
              <div className="w-20 h-20 mx-auto bg-green-100 rounded-full flex items-center justify-center mb-4">
                <CheckCircle2 className="w-10 h-10 text-green-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Voucher Created!</h3>
              <p className="text-gray-600 mb-4">
                Your gold redemption voucher is ready.
              </p>
              
              <div className="bg-amber-50 rounded-xl p-4 mb-4 text-left border border-amber-200">
                <div className="flex justify-between mb-2">
                  <span className="text-gray-600">Voucher ID</span>
                  <span className="font-bold text-amber-700">{redeemResult.voucher_id}</span>
                </div>
                <div className="flex justify-between mb-2">
                  <span className="text-gray-600">Gold Amount</span>
                  <span className="font-semibold">{redeemResult.gram_value} grams</span>
                </div>
                <div className="flex justify-between mb-2">
                  <span className="text-gray-600">Milli&apos;s Redeemed</span>
                  <span className="font-semibold">{redeemResult.milli_redeemed}</span>
                </div>
                <div className="flex justify-between mb-2">
                  <span className="text-gray-600">Valid Until</span>
                  <span className="font-medium text-amber-600">
                    {new Date(redeemResult.expires_at).toLocaleDateString('en-IN', { 
                      day: 'numeric', month: 'short', year: 'numeric' 
                    })}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">New Balance</span>
                  <span className="font-semibold">{redeemResult.new_balance?.toFixed(2)} Milli&apos;s</span>
                </div>
              </div>
              
              <div className="bg-blue-50 rounded-xl p-3 mb-4 border border-blue-200">
                <p className="text-xs text-blue-800">
                  <strong>Note:</strong> {redeemResult.note}
                </p>
              </div>
              
              <div className="flex gap-3">
                <button
                  onClick={() => navigate('/transaction-history')}
                  className="flex-1 py-3 bg-[#F5E6C8] hover:bg-[#EDD9B5] text-[#8B6914] font-medium rounded-xl"
                >
                  View History
                </button>
                <button
                  onClick={handleNewRedeem}
                  className="flex-1 py-3 bg-gradient-to-r from-[#B8860B] to-[#DAA520] text-white font-semibold rounded-xl"
                >
                  New Redemption
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
            <div className="text-center mb-4">
              <div className="w-16 h-16 mx-auto bg-amber-100 rounded-full flex items-center justify-center mb-3">
                <Gem className="w-8 h-8 text-amber-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900">Confirm Redemption</h3>
            </div>
            
            <div className="bg-amber-50 rounded-xl p-4 mb-4">
              <div className="flex justify-between mb-2">
                <span className="text-gray-600">Milli&apos;s to Redeem</span>
                <span className="font-semibold">{millis} Milli&apos;s</span>
              </div>
              <div className="flex justify-between mb-2">
                <span className="text-gray-600">Gold Weight</span>
                <span className="font-semibold">{millisInGrams.toFixed(3)} grams</span>
              </div>
              <div className="flex justify-between border-t border-amber-200 pt-2 mt-2">
                <span className="text-gray-700 font-medium">Estimated Value</span>
                <span className="font-bold text-green-600">₹{goldValue.toFixed(2)}</span>
              </div>
            </div>
            
            <div className="bg-blue-50 rounded-xl p-3 mb-4 border border-blue-200">
              <p className="text-xs text-blue-800">
                A gold voucher will be created. Present this voucher at any Mothers&apos; Gold showroom to collect your jewellery. 
                Voucher is valid for 30 days.
              </p>
            </div>
            
            <div className="flex gap-3">
              <button
                onClick={() => setShowConfirmModal(false)}
                className="flex-1 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-xl"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmRedeem}
                className="flex-1 py-3 bg-gradient-to-r from-[#B8860B] to-[#DAA520] text-white font-semibold rounded-xl"
              >
                Create Voucher
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="px-3 sm:px-6 lg:px-8 py-4 sm:py-6 lg:py-8 min-h-[calc(100vh-80px)] flex flex-col">
        <div className="flex-1 flex flex-col">
          {/* Hero Section - Similar to Book Gold alignment */}
          <div className="mb-6 sm:mb-8">
            {/* Title Section */}
            <div className="mt-2 sm:mt-4 lg:mt-6 mb-6">
              <h1 
                className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-normal leading-tight tracking-wide text-[#B8860B] mb-2 sm:mb-3" 
                style={{ fontFamily: 'Georgia, serif' }}
              >
                Redeem Gold
              </h1>
              <p className="text-gray-600 text-sm sm:text-base lg:text-lg max-w-xl">
                Convert your Milli&apos;s to gold jewellery at our showrooms.
              </p>
            </div>
          </div>

          {/* Main Grid - Input, Summary */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 lg:gap-6 max-w-4xl">
            {/* Left Column - Enter Millis + Mini Balance Card */}
            <div className="space-y-4">
              {/* Enter Milli's Card */}
              <div className="bg-white/90 rounded-2xl p-5 shadow-sm border border-amber-100">
                <h2 className="text-lg font-semibold text-gray-900 mb-3">Enter Milli&apos;s to Redeem</h2>
                
                {/* Milli's Input */}
                <div className="relative mb-3">
                  <input
                    type="text"
                    value={customMillis}
                    onChange={(e) => handleMillisChange(e.target.value)}
                    className="w-full px-4 py-3 text-xl font-semibold text-gray-900 border border-amber-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#B8860B] focus:border-transparent pr-24 bg-white"
                    placeholder="Enter milli's"
                  />
                  <span className="absolute right-4 top-1/2 -translate-y-1/2 text-sm text-gray-500">Milli&apos;s</span>
                </div>

                {/* Preset Amounts - Gold buttons */}
                <div className="grid grid-cols-4 gap-2 mb-4">
                  {presetMillis.map((preset) => (
                    <button
                      key={preset}
                      onClick={() => handlePresetClick(preset)}
                      disabled={preset > userMilliBalance}
                      className={`py-2 sm:py-2.5 rounded-lg font-medium text-xs sm:text-sm transition-all ${
                        millis === preset
                          ? 'bg-gradient-to-r from-[#B8860B] to-[#DAA520] text-white shadow-md'
                          : preset > userMilliBalance
                            ? 'bg-gray-100 text-gray-400 border border-gray-200 cursor-not-allowed'
                            : 'bg-[#F5E6C8] text-[#8B6914] border border-[#B8860B]/30 hover:bg-[#EDD9B5]'
                      }`}
                    >
                      {preset >= 1000 ? `${preset/1000}K` : preset}
                    </button>
                  ))}
                </div>

                {/* Minimum Note */}
                <p className="text-xs text-amber-600 mb-4 flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3" />
                  Minimum 1000 Milli&apos;s (1 gram) required for redemption
                </p>

                {/* Create Voucher Button */}
                <button
                  onClick={handleContinue}
                  disabled={isLoading || millis < 1000 || millis > userMilliBalance || rateLoading}
                  className="w-full py-4 bg-gradient-to-r from-[#B8860B] to-[#DAA520] text-white text-lg font-semibold rounded-xl shadow-lg hover:shadow-xl ring-2 ring-[#B8860B]/30 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    'Create Voucher'
                  )}
                </button>
                
                {/* Security Note */}
                <div className="flex items-center justify-center gap-2 mt-3 text-gray-600">
                  <Lock className="w-4 h-4" />
                  <p className="text-sm font-medium">Voucher valid for 30 days</p>
                </div>
              </div>

              {/* Mini Milli Balance Card */}
              <div 
                className="rounded-xl p-4 shadow-[0_4px_20px_rgba(0,0,0,0.15),0_2px_8px_rgba(0,0,0,0.1)] relative overflow-hidden"
                style={{
                  backgroundImage: 'url(/assets-new/balance-card-bg.webp)',
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                }}
              >
                <div className="flex items-center justify-between relative z-10">
                  <div>
                    <p className="text-white/70 text-xs font-medium mb-1">Available to Redeem</p>
                    <p className="text-2xl font-bold text-white">
                      {userMilliBalance.toFixed(2)} <span className="text-sm font-normal text-white/80">Milli&apos;s</span>
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-white/60 text-xs mb-1">Gold Weight</p>
                    <p className="text-lg font-semibold text-[#FFD700]">
                      {userMilliInGrams.toFixed(4)} g
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Card - Redemption Summary (Gold Theme) */}
            <div className="relative bg-[#F5E6C8] rounded-xl p-6 shadow-[inset_0_0_25px_rgba(139,90,43,0.12)] border-2 border-amber-400/60 flex flex-col">
              {/* Decorative corner accents */}
              <div className="absolute top-2 left-2 w-4 h-4 border-t-2 border-l-2 border-amber-600/70 rounded-tl-sm"></div>
              <div className="absolute top-2 right-2 w-4 h-4 border-t-2 border-r-2 border-amber-600/70 rounded-tr-sm"></div>
              <div className="absolute bottom-2 left-2 w-4 h-4 border-b-2 border-l-2 border-amber-600/70 rounded-bl-sm"></div>
              <div className="absolute bottom-2 right-2 w-4 h-4 border-b-2 border-r-2 border-amber-600/70 rounded-br-sm"></div>
              
              <h2 className="text-xl font-semibold text-amber-900 mb-6 text-center tracking-wide" style={{ fontFamily: 'Georgia, serif' }}>
                Redemption Summary
              </h2>
              
              {/* Summary Items */}
            <div className="space-y-3 mb-4">
              <div className="flex justify-between items-center py-2 border-b border-dashed border-amber-400/40">
                <span className="text-amber-800">Milli&apos;s to Redeem</span>
                <span className="font-semibold text-amber-900">{millis} Milli&apos;s</span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-dashed border-amber-400/40">
                <span className="text-amber-800">Gold Weight</span>
                <span className="font-semibold text-amber-900">{millisInGrams.toFixed(4)} grams</span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-dashed border-amber-400/40">
                <span className="text-amber-800">Redemption Type</span>
                <span className="font-semibold text-amber-900">Gold Jewellery</span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-dashed border-amber-400/40">
                <span className="text-amber-800">Balance After Redemption</span>
                <span className="font-bold text-[#004D40]">{(userMilliBalance - millis).toFixed(2)} Milli&apos;s</span>
              </div>
            </div>

            {/* Collapsible Showroom Info */}
            <div className="mb-4">
              <button 
                onClick={() => setShowShowrooms(!showShowrooms)}
                className="w-full flex items-center justify-between text-sm font-semibold text-amber-900 py-2 hover:text-amber-700 transition-colors"
              >
                <span className="flex items-center gap-2">
                  <MapPin className="w-4 h-4" />
                  Available Showrooms
                </span>
                {showShowrooms ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </button>
              {showShowrooms && (
                <div className="space-y-2 mt-2 animate-in slide-in-from-top-2 duration-200">
                  {showrooms.map((showroom, index) => (
                    <div 
                      key={index} 
                      className="bg-amber-100/50 rounded-lg border border-amber-300/40 overflow-hidden"
                    >
                      <button
                        onClick={() => setExpandedShowroom(expandedShowroom === index ? null : index)}
                        className="w-full p-3 flex items-center justify-between text-left hover:bg-amber-100/70 transition-colors"
                      >
                        <span className="text-sm font-medium text-amber-900">{showroom.name}</span>
                        {expandedShowroom === index ? (
                          <ChevronUp className="w-4 h-4 text-amber-700" />
                        ) : (
                          <ChevronDown className="w-4 h-4 text-amber-700" />
                        )}
                      </button>
                      {expandedShowroom === index && (
                        <div className="px-3 pb-3 pt-0 animate-in slide-in-from-top-1 duration-150">
                          <p className="text-xs text-amber-700/80 leading-relaxed">{showroom.address}</p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Rotating Tip */}
            <div className="flex-1 flex items-end justify-center">
              <p className="text-xs text-amber-700/80 text-center italic px-2" style={{ fontFamily: 'Georgia, serif' }}>
                {currentTip}
              </p>
            </div>
          </div>
        </div>

        {/* Note Card */}
        <div className="mt-6 max-w-4xl">
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
            <p className="text-sm text-amber-800">
              <span className="font-semibold">Note:</span> Gold vouchers are valid for 30 days from the date of issue. Present the voucher at any Mothers Gold &amp; Diamonds LLP showroom to collect your gold.
            </p>
          </div>
        </div>
      </div>
    </div>
  </>
  );
};

export default RedeemGoldPage;
